/* global Intl -- eslint's browser globals omit it; the admin's utils.js trips the same rule */
// Dates, relative times, numbers and money in the current language. The
// Bosnian branches are the site's old helpers, verbatim — tests hold them to
// the old output character for character. English uses Intl, with the month
// spelled out because US and UK readers disagree on 01/02.

const BOSNIAN_MONTHS = [
	"Januar", "Februar", "Mart", "April", "Maj", "Juni",
	"Juli", "August", "Septembar", "Oktobar", "Novembar", "Decembar",
];

const pad = (n) => String(n).padStart(2, "0");

// Building an Intl formatter is the expensive part of formatting with it, and
// the lists call these once per row. Each formatter is built on first use and
// kept for the life of the page.
const formatters = new Map();

function formatter(Constructor, locale, options) {
	const key = `${Constructor.name}|${locale}|${JSON.stringify(options ?? null)}`;
	let cached = formatters.get(key);
	if (!cached) {
		cached = new Constructor(locale, options);
		formatters.set(key, cached);
	}
	return cached;
}

function toDate(value) {
	if (value === null || value === undefined || value === "") return null;
	const date = value instanceof Date ? value : new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
}

export function formatShortDate(value, language) {
	const date = toDate(value);
	if (!date) return null;
	if (language === "en") {
		return formatter(Intl.DateTimeFormat, "en-GB", { day: "numeric", month: "short", year: "numeric" }).format(date);
	}
	return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
}

export function formatLongDate(value, language) {
	const date = toDate(value);
	if (!date) return null;
	if (language === "en") {
		return formatter(Intl.DateTimeFormat, "en-GB", { day: "numeric", month: "long", year: "numeric" }).format(date);
	}
	return `${date.getDate()}. ${BOSNIAN_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

// Bosnian's three forms for a count: 1, 21, 101 take the singular, 2-4 (but not
// 12-14) the dual, everything else the plural. It is the old getLabel from
// src/utils/helpers.js, verbatim, and only formatTimeAgo's Bosnian branch needs
// it: every other counted phrase is an i18next plural key now.
function getLabel(count, labels) {
	const lastDigit = count % 10;
	const lastTwoDigits = count % 100;

	if (lastDigit === 1 && lastTwoDigits !== 11) {
		return labels.singular;
	} else if (
		[2, 3, 4].includes(lastDigit) &&
		![12, 13, 14].includes(lastTwoDigits)
	) {
		return labels.dual;
	} else {
		return labels.plural;
	}
}

const INTERVALS = [
	{ unit: "year", seconds: 365 * 24 * 60 * 60, bs: { singular: "godinu", dual: "godine", plural: "godina" } },
	{ unit: "month", seconds: 30 * 24 * 60 * 60, bs: { singular: "mjesec", dual: "mjeseca", plural: "mjeseci" } },
	{ unit: "week", seconds: 7 * 24 * 60 * 60, bs: { singular: "sedmicu", dual: "sedmice", plural: "sedmica" } },
	{ unit: "day", seconds: 24 * 60 * 60, bs: { singular: "dan", dual: "dana", plural: "dana" } },
	{ unit: "hour", seconds: 60 * 60, bs: { singular: "sat", dual: "sata", plural: "sati" } },
	{ unit: "minute", seconds: 60, bs: { singular: "minutu", dual: "minute", plural: "minuta" } },
];

export function formatTimeAgo(timestamp, language, now = Date.now()) {
	const time = timestamp instanceof Date ? timestamp.getTime() : Number(timestamp);
	const secondsAgo = Math.floor((now - time) / 1000);
	const english = language === "en";

	if (secondsAgo >= 0) {
		for (const interval of INTERVALS) {
			const count = Math.floor(secondsAgo / interval.seconds);
			if (count < 1) continue;
			if (english) {
				return formatter(Intl.RelativeTimeFormat, "en", { numeric: "always" }).format(-count, interval.unit);
			}
			return `prije ${count} ${getLabel(count, interval.bs)}`;
		}
	}

	return english ? "just now" : "manje od minute";
}

export function formatNumber(value, language) {
	return formatter(Intl.NumberFormat, language === "en" ? "en-GB" : "bs").format(value);
}

// The price cards write the number and the € in separate elements, and
// Bosnian has always shown the raw number with a comma: 14,9 — not 14,90.
export function formatDecimal(amount, language) {
	const written = String(Number(amount));
	return language === "en" ? written : written.replace(".", ",");
}

// The same number for a price card's own element: Bosnian is formatDecimal
// untouched (0,5 and 1), English always writes two decimals (0.50 and 1.00),
// as formatPrice does for the billing page.
export function formatPriceAmount(amount, language) {
	return language === "en" ? Number(amount).toFixed(2) : formatDecimal(amount, language);
}

export function formatPrice(amount, currency, language) {
	if (amount === null || amount === undefined) return null;

	const fixed = Number(amount).toFixed(2);
	if (language === "en") {
		return currency === "EUR" ? `€${fixed}` : `${fixed} ${currency}`;
	}
	const written = fixed.replace(".", ",");
	return currency === "EUR" ? `${written} €` : `${written} ${currency}`;
}
